import { useReducer, useMemo, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { QuoteLineItem } from '../types/api';
import { calculateLineTotal, calculateQuoteTotals } from '../utils/quote-totals';

interface ManagedQuoteLineItem extends QuoteLineItem {
  tempId: string;
  status: 'ACTIVE' | 'PENDING_DELETE' | 'DELETED';
}

type QuoteLineItemAction =
  | { type: 'SET_ITEMS'; payload: { items: QuoteLineItem[] } }
  | { type: 'ADD_ITEM'; payload: { item: Partial<QuoteLineItem> } }
  | { type: 'REMOVE_ITEM'; payload: { tempId: string } }
  | { type: 'UPDATE_QUANTITY'; payload: { tempId: string; quantity: number } }
  | { type: 'UPDATE_FIELD'; payload: { tempId: string; field: string; value: any } }
  | { type: 'MOVE_ITEM_UP'; payload: { tempId: string } }
  | { type: 'MOVE_ITEM_DOWN'; payload: { tempId: string } };

function quoteLineItemReducer(
  state: ManagedQuoteLineItem[],
  action: QuoteLineItemAction
): ManagedQuoteLineItem[] {
  switch (action.type) {
    case 'SET_ITEMS':
      return action.payload.items.map((item, index) => ({
        ...item,
        tempId: item.guid || uuidv4(),
        status: 'ACTIVE' as const,
        sequence: item.sequence ?? index + 1,
      }));

    case 'ADD_ITEM': {
      const { item } = action.payload;
      const tempId = uuidv4();

      const maxSequence = state
        .filter((item) => item.sequence != null)
        .reduce((max, item) => Math.max(max, item.sequence || 0), 0);

      const quantity = item.quantity || 1;
      const unitPrice = item.unitPrice || 0;
      const taxRate = item.taxRate ?? 2000; // Use ?? instead of || to preserve 0 for non-taxable items

      const newItem: ManagedQuoteLineItem = {
        tempId,
        status: 'ACTIVE',
        description: item.description || '',
        sku: item.sku || '',
        quantity,
        unitPrice,
        lineTotal: calculateLineTotal({ quantity, unitPrice, taxRate }),
        taxRate,
        sequence: maxSequence + 1,
        variantGuid: item.variantGuid,
      };

      return [...state, newItem];
    }

    case 'REMOVE_ITEM': {
      const { tempId } = action.payload;
      return state.map((item) => {
        if (item.tempId === tempId) {
          return {
            ...item,
            status: item.id ? ('PENDING_DELETE' as const) : ('DELETED' as const),
          };
        }
        return item;
      });
    }

    case 'UPDATE_QUANTITY': {
      const { tempId, quantity } = action.payload;
      return state.map((item) => {
        if (item.tempId === tempId) {
          const newQuantity = Math.max(quantity, 1);
          return {
            ...item,
            quantity: newQuantity,
            lineTotal: calculateLineTotal({ ...item, quantity: newQuantity }),
          };
        }
        return item;
      });
    }

    case 'UPDATE_FIELD': {
      const { tempId, field, value } = action.payload;
      return state.map((item) => {
        if (item.tempId === tempId) {
          const updated = {
            ...item,
            [field]: value,
          };

          // Recalculate lineTotal if quantity or unitPrice changed
          if (field === 'quantity' || field === 'unitPrice' || field === 'taxRate') {
            updated.lineTotal = calculateLineTotal(updated);
          }

          return updated;
        }
        return item;
      });
    }

    case 'MOVE_ITEM_UP': {
      const { tempId } = action.payload;
      const activeItems = state.filter((item) => item.status === 'ACTIVE');
      activeItems.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

      const currentIndex = activeItems.findIndex((item) => item.tempId === tempId);
      if (currentIndex <= 0) return state;

      const currentItem = activeItems[currentIndex];
      const aboveItem = activeItems[currentIndex - 1];

      return state.map((item) => {
        if (item.tempId === currentItem.tempId) {
          return { ...item, sequence: aboveItem.sequence };
        }
        if (item.tempId === aboveItem.tempId) {
          return { ...item, sequence: currentItem.sequence };
        }
        return item;
      });
    }

    case 'MOVE_ITEM_DOWN': {
      const { tempId } = action.payload;
      const activeItems = state.filter((item) => item.status === 'ACTIVE');
      activeItems.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

      const currentIndex = activeItems.findIndex((item) => item.tempId === tempId);
      if (currentIndex < 0 || currentIndex >= activeItems.length - 1) return state;

      const currentItem = activeItems[currentIndex];
      const belowItem = activeItems[currentIndex + 1];

      return state.map((item) => {
        if (item.tempId === currentItem.tempId) {
          return { ...item, sequence: belowItem.sequence };
        }
        if (item.tempId === belowItem.tempId) {
          return { ...item, sequence: currentItem.sequence };
        }
        return item;
      });
    }

    default:
      return state;
  }
}

export function useQuoteLineItems(initialItems: QuoteLineItem[] = []) {
  const [items, dispatch] = useReducer(quoteLineItemReducer, [], () =>
    initialItems.map((item, index) => ({
      ...item,
      tempId: item.guid || uuidv4(),
      status: 'ACTIVE' as const,
      sequence: item.sequence ?? index + 1,
    }))
  );

  const addItem = useCallback((item: Partial<QuoteLineItem>) => {
    dispatch({ type: 'ADD_ITEM', payload: { item } });
  }, []);

  const removeItem = useCallback((tempId: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: { tempId } });
  }, []);

  const updateQuantity = useCallback((tempId: string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { tempId, quantity } });
  }, []);

  const updateField = useCallback((tempId: string, field: string, value: any) => {
    dispatch({ type: 'UPDATE_FIELD', payload: { tempId, field, value } });
  }, []);

  const moveItemUp = useCallback((tempId: string) => {
    dispatch({ type: 'MOVE_ITEM_UP', payload: { tempId } });
  }, []);

  const moveItemDown = useCallback((tempId: string) => {
    dispatch({ type: 'MOVE_ITEM_DOWN', payload: { tempId } });
  }, []);

  const setItems = useCallback((newItems: QuoteLineItem[]) => {
    dispatch({ type: 'SET_ITEMS', payload: { items: newItems } });
  }, []);

  // Computed values
  const summary = useMemo(() => {
    const activeItems = items.filter((item) => item.status === 'ACTIVE');

    return {
      activeItems,
      ...calculateQuoteTotals(activeItems),
      itemCount: activeItems.length,
    };
  }, [items]);

  const visibleItems = useMemo(
    () =>
      items
        .filter((item) => item.status !== 'DELETED')
        .sort((a, b) => (a.sequence || 0) - (b.sequence || 0)),
    [items]
  );

  const activeItems = useMemo(
    () =>
      items
        .filter((item) => item.status === 'ACTIVE')
        .sort((a, b) => (a.sequence || 0) - (b.sequence || 0)),
    [items]
  );

  // The server replaces all of a quote's line items on save, so only send
  // ACTIVE ones; anything left out (PENDING_DELETE) is deleted.
  const getCleanItems = useCallback((): QuoteLineItem[] => {
    const itemsToSubmit = items.filter((item) => item.status === 'ACTIVE');

    return itemsToSubmit.map((item) => {
      const { tempId, status, ...cleanItem } = item;

      if (cleanItem.id && cleanItem.id < 0) {
        delete cleanItem.id;
      }

      return cleanItem as QuoteLineItem;
    });
  }, [items]);

  return {
    items: visibleItems,
    activeItems,
    summary,
    addItem,
    removeItem,
    updateQuantity,
    updateField,
    moveItemUp,
    moveItemDown,
    setItems,
    getCleanItems,
  };
}
